import { captureException } from "@sentry/electron/renderer";
import { intersection, mapValues } from "lodash";
import mixpanel from "mixpanel-browser";

import { getBrands } from "../../../../commons/libs/content-service";
import { getEnv } from "../../../../commons/libs/env-service";
import { getSettings } from "../../../../commons/libs/settings-service";
import {
  getConfiguredVeloconnectEndpointBrandKeys,
  getVeloconnectEndpointBrandKeys,
  getVeloconnectEndpoints
} from "../../../../commons/libs/veloconnect-service";
import { getWawiConnectionInfo } from "../../../../commons/libs/wawi-service";
import { Currency } from "../../../../commons/types/currency";
import { Env } from "../../../../commons/types/env";
import {
  AssortmentSettings,
  AutomaticAssortmentFilter,
  CustomizationSettings
} from "../../../../commons/types/settings";
import initCommonMixpanel, { MixpanelConfig } from "../../../commons/libs/externals/mixpanel";
import { getStoredAuthenticationCredentials } from "../credentials";

interface UserProfile
  extends
    Required<Omit<CustomizationSettings, "dealerWebsite" | "noPinRequiredForDealerWebsite" | "wallpaper">>,
    Pick<Env, "customerGroup" | "serviceTag" | "locale" | "updatersActive"> {
  $name: Env["serviceTag"]; // Reserved Property for Mixpanel People/User profile
  licence: Env["license"];
  appVersion: string;
  currency: Currency;
  numBookmarks: number;
  printingActive: boolean;
  mailActive: boolean;
  features: string[];
  brands: string[];
  highlightedBrands: string[];
  numBrands: number;
  numHighlightedBrands: number;
  brandsWithActiveModelYears: Record<string, number[]>;
  isWawiConfigured: boolean;
  availableVeloconnectBrands?: string[];
  numAvailableVeloconnectBrands?: number;
  configuredVeloconnectBrands?: string[];
  numConfiguredVeloconnectBrands?: number;
  assortmentType: AssortmentSettings["type"];
  automaticAssortmentFilter: AutomaticAssortmentFilter | null;
  assortmentPriceSettings: AssortmentSettings["priceSettings"];
  assortmentProductFinderFilterSettings: AssortmentSettings["productFinderFilterSettings"];
  initialSetupFirstFinished: Date | null;
}

const OUTDATED_USER_PROFILE_PROPS = [
  "theme",
  "availableVeloConnectBrands",
  "numAvailableVeloConnectBrands",
  "configuredVeloConnectBrands",
  "numConfiguredVeloConnectBrands",
  "configuredVeloconnectProxyBrands",
  "numConfiguredVeloconnectProxyBrands"
];

const fetchSystemServiceInfo = async <RT>(callback: Promise<RT>): Promise<RT | undefined> => {
  try {
    return await callback;
  } catch (error) {
    const message = "Couldn't fetch system service info during mixpanel initialization trackUserProfile.";
    console.error(message, error);
    captureException(error, { extra: { message } });
  }
};

const initMixpanel = ({ appVersion, sentry, config }: MixpanelConfig) => {
  const commonMixpanel = initCommonMixpanel({ appVersion, sentry, config });

  /**
   * Sets the Mixpanel People user profile with mobile-specific data.
   * Called on app init after `trackUserProfile` registers super properties.
   */
  const setUserProfile = async () => {
    const env = await getEnv();
    const settings = await fetchSystemServiceInfo(getSettings());

    if (!settings) {
      return Promise.resolve();
    }

    const isWawiConfigured = !!(await fetchSystemServiceInfo(getWawiConnectionInfo()))?.type;
    const serviceTag = env.serviceTag;

    const allBrands = await fetchSystemServiceInfo(getBrands());
    const activeBrands = (allBrands ?? []).filter(brand => brand.active);

    const brands = activeBrands.map(brand => brand.key);
    const highlightedBrands = activeBrands.filter(brand => brand.highlighted).map(brand => brand.key);
    const brandsWithActiveModelYears = activeBrands.reduce(
      (acc, { key, modelYears }) => {
        acc[key] = modelYears ?? [];
        return acc;
      },
      {} as UserProfile["brandsWithActiveModelYears"]
    );

    const features = (Object.keys(settings.features) as (keyof typeof settings.features)[]).filter(
      key => !!settings.features[key]
    );
    const authenticationCredentials = getStoredAuthenticationCredentials();
    const {
      dealerWebsite: _dealerWebsite,
      noPinRequiredForDealerWebsite: _noPinRequiredForDealerWebsite,
      wallpaper: _wallpaper,
      ...sanitizedCustomizationSettings
    } = settings.customization;

    const veloconnectEndpoints = await fetchSystemServiceInfo(getVeloconnectEndpoints());
    const configuredVeloconnectBrands = veloconnectEndpoints
      ? getConfiguredVeloconnectEndpointBrandKeys(veloconnectEndpoints)
      : undefined;
    const availableVeloconnectBrands = veloconnectEndpoints
      ? getVeloconnectEndpointBrandKeys(veloconnectEndpoints)
      : undefined;

    const userProfile: UserProfile = {
      ...sanitizedCustomizationSettings,
      bikeCenterName: sanitizedCustomizationSettings.bikeCenterName ?? "",

      $name: serviceTag, // Reserved Property for Mixpanel People/User profile
      serviceTag,
      customerGroup: env.customerGroup,
      licence: env.license,
      locale: env.locale,
      updatersActive: env.updatersActive,
      appVersion,
      numBookmarks: settings.bookmarks.persistent.length,
      currency: settings.currency,
      printingActive: settings.printing.active,
      mailActive: settings.mail.active,
      features,
      brands,
      highlightedBrands,
      numBrands: brands.length,
      numHighlightedBrands: highlightedBrands.length,
      brandsWithActiveModelYears,
      isWawiConfigured,
      assortmentType: settings.assortment.type,
      automaticAssortmentFilter: settings.assortment["automatic"]?.filter ?? null,
      assortmentPriceSettings: settings.assortment.priceSettings,
      assortmentProductFinderFilterSettings: settings.assortment.productFinderFilterSettings,
      initialSetupFirstFinished: settings.initialSetup.firstFinished
        ? new Date(settings.initialSetup.firstFinished)
        : null,

      /*
       * Following properties should be set only if they are available.
       * This is to avoid setting them to null in mixpanel and overwriting the existing values.
       * These properties depend on the availability of our Veloconnect Proxy, which could be down at any time.
       */
      ...(availableVeloconnectBrands
        ? {
            availableVeloconnectBrands: intersection(availableVeloconnectBrands, brands),
            numAvailableVeloconnectBrands: intersection(availableVeloconnectBrands, brands).length
          }
        : {}),
      ...(configuredVeloconnectBrands
        ? {
            configuredVeloconnectBrands,
            numConfiguredVeloconnectBrands: configuredVeloconnectBrands.length
          }
        : {})
    };

    mixpanel.identify(env.serviceTag);
    mixpanel.alias(`${env.serviceTag}-${authenticationCredentials.licenseId}`, env.serviceTag);

    // Replace undefined values with null, to keep unset values explicitly in mixpanel
    mixpanel.people.set(mapValues(userProfile, value => (value === undefined ? null : value)));

    mixpanel.people.set_once("firstSeen", new Date());

    // Remove outdated user profile properties that have ever been set
    mixpanel.people.unset(OUTDATED_USER_PROFILE_PROPS);
  };

  return {
    ...commonMixpanel,
    setUserProfile
  };
};

export default initMixpanel;
